import React, { useState, useEffect } from 'react';
import { studentAPI, adminAPI } from '../services/api'; // Update this line
import AdminSidebar from '../components/AdminSidebar';
import AdminNavbar from '../components/AdminNavbar';
import { useSidebar } from '../components/SidebarContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

const AddStudent = () => {
  const { isSidebarOpen = true } = useSidebar() || {};
  const sidebarWidth = isSidebarOpen ? '250px' : '70px';

  const [studentData, setStudentData] = useState({
    username: '',
    email: '',
    password: '',
    fullName: '',
    admissionNo: '',
    collegeName: '',
    accountType: 'adminCreated',
  });

  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState('No file chosen');
  const [fileSize, setFileSize] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewStudent, setViewStudent] = useState(null);
  const [notification, setNotification] = useState({ message: '', type: '' });
  const [modalNotification, setModalNotification] = useState({ message: '', type: '' });
  const [colleges, setColleges] = useState([]);
  const [loadingColleges, setLoadingColleges] = useState(false);

  // Add this useEffect after the existing ones
useEffect(() => {
  const fetchColleges = async () => {
    setLoadingColleges(true);
    try {
      const collegeList = await adminAPI.getColleges();
      setColleges(collegeList);
    } catch (error) {
      console.error('Error fetching colleges:', error);
      showNotification('Failed to load colleges', 'error');
    } finally {
      setLoadingColleges(false);
    }
  };

  fetchColleges();
}, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (notification.message) {
      const timer = setTimeout(() => {
        setNotification({ message: '', type: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  useEffect(() => {
    if (modalNotification.message) {
      const timer = setTimeout(() => {
        setModalNotification({ message: '', type: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [modalNotification]);

  const showNotification = (message, type = 'error') => {
    setNotification({ message, type });
  };

  const showModalNotification = (message, type = 'error') => {
    setModalNotification({ message, type });
  };

  const handleCloseNotification = () => {
    setNotification({ message: '', type: '' });
  };

  const handleCloseModalNotification = () => {
    setModalNotification({ message: '', type: '' });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setStudentData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        showNotification('Invalid CSV');
        return;
      }
      setSelectedFile(file);
      setFileName(file.name);
      setFileSize((file.size / 1024).toFixed(2));
    } else {
      setSelectedFile(null);
      setFileName('No file chosen');
      setFileSize(null);
    }
  };

  const handleFileClear = () => {
    setSelectedFile(null);
    setFileName('No file chosen');
    setFileSize(null);
    document.getElementById('csvFile').value = '';
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      showNotification('Please select a CSV file to upload');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (event) => {
      setLoading(true);
      try {
        const rows = event.target.result.split('\n').filter((row) => row.trim());
        if (rows.length < 2) {
          showNotification('Invalid CSV');
          return;
        }

        const expectedHeaders = 'username,email,password,fullName,admissionNo,collegeName';
        if (rows[0].trim() !== expectedHeaders) {
          showNotification('Invalid CSV');
          return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const validStudents = [];
        let hasError = false;

        rows.slice(1).filter((row) => row.trim()).forEach((row) => {
          const fields = row.split(',').map((field) => field.trim().replace(/^"|"$/g, ''));

          if (fields.length !== 6) {
            hasError = true;
            return;
          }

          const [username, email, password, fullName, admissionNo, collegeName] = fields;

          if (!username || !password || !email || !fullName || !admissionNo || !collegeName) {
            hasError = true;
            return;
          }

          if (!emailRegex.test(email)) {
            hasError = true;
            return;
          }

          validStudents.push({
            username,
            email,
            password,
            fullName,
            admissionNo,
            collegeName,
            accountType: 'adminCreated',
          });
        });

        if (hasError || validStudents.length === 0) {
          showNotification('Invalid CSV');
          return;
        }

        let successCount = 0;
        for (const student of validStudents) {
          try {
            await studentAPI.register(student);
            successCount++;
          } catch (error) {
            let errorMessage = 'Error adding student';
            if (error.response) {
              if (error.response.status === 400) {
                errorMessage = error.response.data.message || 'Student with this email, username, or admission number already exists.';
                if (errorMessage.includes('username')) errorMessage = 'Username already taken';
              } else if (error.response.status === 404) {
                errorMessage = 'Student registration endpoint not found';
              } else {
                errorMessage = error.response.data.message || 'Server error. Please try again later.';
              }
            } else {
              errorMessage = error.message || 'Network error. Please check your connection.';
            }
            showNotification(`${errorMessage} for ${student.username}`, 'error');
          }
        }

        const message = `${successCount} student${successCount !== 1 ? 's' : ''} added successfully`;
        showNotification(message, 'success');
        handleFileClear();
      } catch (error) {
        let errorMessage = 'Error uploading students';
        if (error.response) {
          if (error.response.status === 400) {
            errorMessage = error.response.data.message || 'Invalid data provided. Please check the CSV file.';
          } else if (error.response.status === 404) {
            errorMessage = 'Bulk upload endpoint not found. Please check the server configuration.';
          } else if (error.response.status === 401) {
            errorMessage = 'Authentication required. Please log in again.';
          } else {
            errorMessage = error.response.data.message || 'Server error. Please try again later.';
          }
        } else {
          errorMessage = error.message || 'Network error. Please check your connection.';
        }
        showNotification(errorMessage, 'error');
      } finally {
        setLoading(false);
      }
    };
    reader.readAsText(selectedFile);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!Object.values(studentData).every((val) => val && typeof val === 'string' && val.trim() !== '')) {
      showModalNotification('Please fill all fields with valid data', 'error');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(studentData.email)) {
      showModalNotification('Invalid email format', 'error');
      return;
    }

    setLoading(true);
    try {
      const dataToSend = { ...studentData, accountType: 'adminCreated' };
      await studentAPI.register(dataToSend);
      setShowModal(false);
      showNotification('Student added successfully', 'success');
      setStudentData({
        username: '',
        email: '',
        password: '',
        fullName: '',
        admissionNo: '',
        collegeName: '',
        accountType: 'adminCreated',
      });
    } catch (error) {
      let errorMessage = 'Error adding student';
      if (error.response) {
        if (error.response.status === 400) {
          errorMessage = error.response.data.message || 'Student with this email, username, or admission number already exists.';
          if (errorMessage.includes('username')) {
            errorMessage = 'Username already taken';
          }
        } else if (error.response.status === 404) {
          errorMessage = 'Student registration endpoint not found';
        } else {
          errorMessage = error.response.data.message || 'Server error. Please try again later.';
        }
      } else {
        errorMessage = error.message || 'Network error. Please check your connection.';
      }
      showModalNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = () => {
    const csvContent = [
      'username,email,password,fullName,admissionNo,collegeName',
      'demo1,demo1@example.com,Pass1234,Demo1,DEM001,DemoATC College',
      'demo2,demo2@example.com,Pass5678,Demo2,DEM002,DemoATC College',
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'student_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleOpenModal = () => {
    setShowModal(true);
    setStudentData({
      username: '',
      email: '',
      password: '',
      fullName: '',
      admissionNo: '',
      collegeName: '',
      accountType: 'adminCreated',
    });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalNotification({ message: '', type: '' });
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setViewStudent(null);
  };

  const styles = {
    container: {
      display: 'flex',
      height: '100vh',
      overflow: 'hidden',
      minHeight: '100vh',
      background: 'linear-gradient(to right, #EFF6FF, #F3E8FF, #FEE7EF)',
      fontFamily: "'Roboto', sans-serif",
    },
    sidebar: {
      width: sidebarWidth,
      borderRadius: '30px',
      height: '100vh',
      flexShrink: 0,
      fontSize: '16px',
      fontFamily: "'Roboto', sans-serif",
    },
    content: {
      flex: 1,
      padding: '15px',
      margin: '6rem 2rem 2rem 2rem',
      backgroundColor: 'white',
      transition: 'margin-left 0.3s ease',
      minHeight: 'calc(100vh - 8rem)',
      maxHeight: 'calc(100vh - 8rem)',
      borderRadius: '1rem',
      maxWidth: `calc(100vw - ${sidebarWidth} - 4rem)`,
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      fontFamily: "'Roboto', sans-serif",
    },
    scrollableContent: {
      flex: 1,
      width: '100%',
      overflowY: 'auto', // Changed from 'hidden' to 'auto' to enable scrollbar
      paddingBottom: '1rem',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '16px',
      position: 'relative',
      fontFamily: "'Roboto', sans-serif",
      maxHeight: 'calc(100% - 100px)', // Increased scrollable area by setting a max height
    },
    cardContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '16px',
      width: '100%',
      maxWidth: '500px',
      margin: '0 auto',
      fontFamily: "'Roboto', sans-serif",
      position: 'relative',
      minHeight: '0', // Ensure no extra height causes overflow
    },
    notificationContainer: {
      position: 'absolute',
      top: '8px',
      right: '8px',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      width: '250px',
      zIndex: 100,
      fontFamily: "'Roboto', sans-serif",
    },
    modalNotificationContainer: {
      position: 'absolute',
      top: '15px',
      right: '15px',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      width: '180px',
      zIndex: 100,
      fontFamily: "'Roboto', sans-serif",
    },
    notification: {
      fontSize: '0.85rem',
      color: '#6B21A8',
      fontFamily: "'Roboto', sans-serif",
      fontWeight: '400',
      backgroundColor: '#F3E8FF',
      padding: '5px 10px',
      borderRadius: '4px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      animation: 'slideInFromRight 0.5s ease-out',
      wordBreak: 'break-word',
      overflowX: 'hidden',
      minHeight: 'auto',
      maxHeight: 'none',
    },
    notificationClose: {
      cursor: 'pointer',
      fontSize: '0.75rem',
      color: '#6B21A8',
      fontFamily: "'Roboto', sans-serif",
    },
    button: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0.5rem 1.2rem',
      fontSize: '0.875rem',
      fontWeight: '600',
      color: '#6B21A8',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      cursor: 'pointer',
      borderRadius: '1.5rem',
      border: '2px solid transparent',
      backgroundImage: 'linear-gradient(#fff, #fff), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      transition: 'all 0.4s ease',
      whiteSpace: 'nowrap',
      fontFamily: "'Roboto', sans-serif",
    },
    buttonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
      fontFamily: "'Roboto', sans-serif",
    },
    uploadContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      border: '2px dashed #C5A8FF',
      borderRadius: '0.375rem',
      padding: '2rem',
      backgroundColor: '#F3F4F6',
      width: '100%',
      maxWidth: '500px',
      minHeight: '180px',
      cursor: 'pointer',
      transition: 'background-color 0.3s ease',
      margin: '16px auto',
      fontFamily: "'Roboto', sans-serif",
      overflow: 'hidden', // Prevent overflow within upload container
    },
    uploadText: {
      fontSize: '1rem',
      fontWeight: '500',
      color: '#6B21A8',
      textAlign: 'center',
      marginBottom: '0.5rem',
      fontFamily: "'Roboto', sans-serif",
    },
    uploadSubText: {
      fontSize: '0.875rem',
      color: '#808080',
      textAlign: 'center',
      fontFamily: "'Roboto', sans-serif",
    },
    filePreview: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: '#F3F4F6',
      border: '1px solid #D1D5DB',
      borderRadius: '0.375rem',
      padding: '0.5rem 1rem',
      width: '100%',
      maxWidth: '500px',
      boxSizing: 'border-box',
      margin: '0 auto',
      fontFamily: "'Roboto', sans-serif",
      overflow: 'hidden', // Prevent overflow within file preview
    },
    fileInfo: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.25rem',
      flex: 1,
      fontFamily: "'Roboto', sans-serif",
      overflow: 'hidden', // Prevent overflow within file info
    },
    fileNameText: {
      fontSize: '0.875rem',
      color: '#6B21A8',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      fontFamily: "'Roboto', sans-serif",
    },
    progressBarContainer: {
      width: '100%',
      height: '5px',
      backgroundColor: '#E5E7EB',
      borderRadius: '5px',
      overflow: 'hidden',
      fontFamily: "'Roboto', sans-serif",
    },
    progressBar: {
      width: '100%',
      height: '100%',
      backgroundColor: '#6B21A8',
      transition: 'width 0.3s ease',
      fontFamily: "'Roboto', sans-serif",
    },
    fileActions: {
      display: 'flex',
      gap: '0.5rem',
      fontFamily: "'Roboto', sans-serif",
    },
    actionButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      fontSize: '1rem',
      padding: '0.25rem',
      fontFamily: "'Roboto', sans-serif",
    },
    label: {
      fontSize: '0.875rem',
      fontWeight: '500',
      color: '#6B21A8',
      marginBottom: '0.25rem',
      display: 'block',
      textAlign: 'start',
      fontFamily: "'Roboto', sans-serif",
    },
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      fontFamily: "'Roboto', sans-serif",
    },
    modalContent: {
      backgroundColor: '#fff',
      borderRadius: '0.75rem',
      boxShadow: '0 6px 12px rgba(0, 0, 0, 0.15)',
      padding: '2rem 3rem',
      height: '400px',
      width: '37%',
      boxSizing: 'border-box',
      position: 'relative',
      fontFamily: "'Roboto', sans-serif",
    },
    viewModalContent: {
      backgroundColor: '#fff',
      borderRadius: '0.75rem',
      boxShadow: '0 6px 12px rgba(0, 0, 0, 0.15)',
      padding: '2rem 3rem',
      width: '37%',
      boxSizing: 'border-box',
      maxHeight: '90vh',
      overflowY: 'auto',
      position: 'relative',
      fontFamily: "'Roboto', sans-serif",
    },
    modalHeader: {
      fontSize: '1.25rem',
      fontWeight: '500',
      color: '#6B21A8',
      textAlign: 'left',
      marginBottom: '0.5rem',
      fontFamily: "'Roboto', sans-serif",
    },
    formGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
      gap: '1.5rem',
      marginBottom: '2.5rem',
      fontFamily: "'Roboto', sans-serif",
    },
    viewGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
      gap: '1rem 1.5rem',
      marginBottom: '1rem',
      fontFamily: "'Roboto', sans-serif",
    },
    input: {
      width: '100%',
      padding: '0.6rem',
      border: '1px solid #D1D5DB',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      transition: 'border-color 0.3s ease',
      boxSizing: 'border-box',
      fontFamily: "'Roboto', sans-serif",
    },
    viewField: {
      fontSize: '0.875rem',
      color: '#1F2937',
      padding: '0.6rem',
      border: '1px solid #D1D5DB',
      borderRadius: '0.375rem',
      backgroundColor: '#F9FAFB',
      fontFamily: "'Roboto', sans-serif",
    },
    modalButtonContainer: {
      position: 'absolute',
      bottom: '24px',
      right: '45px',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '0.85rem',
      zIndex: 1001,
      fontFamily: "'Roboto', sans-serif",
    },
    modalCancelButton: {
      backgroundColor: '#D1D5DB',
      color: '#374151',
      padding: '5px 15px',
      border: 'none',
      borderRadius: '30px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      fontFamily: "'Roboto', sans-serif",
    },
    modalAddButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '5px 15px',
      fontSize: '14px',
      fontWeight: '500',
      color: '#6B21A8',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      cursor: 'pointer',
      borderRadius: '30px',
      border: '2px solid transparent',
      backgroundImage: 'linear-gradient(#fff, #fff), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      transition: 'all 0.4s ease',
      fontFamily: "'Roboto', sans-serif",
    },
    modalCloseButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '6px 16px',
      fontSize: '14px',
      fontWeight: '500',
      color: '#6B21A8',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      cursor: 'pointer',
      borderRadius: '30px',
      border: '2px solid transparent',
      backgroundImage: 'linear-gradient(#fff, #fff), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      transition: 'all 0.4s ease',
      fontFamily: "'Roboto', sans-serif",
    },
  };

  return (
    <div style={styles.container}>
      <style>
        {`
          html, body {
            margin: 0;
            padding: 0;
            height: 100vh;
            width: 100vw;
            overflow-x: hidden;
          }
         
          @keyframes slideInFromRight {
            0% { transform: translateX(100%); opacity: 0; }
            100% { transform: translateX(0); opacity: 1; }
          }
          .glow-button:hover:not(:disabled) {
            background: linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF);
            transform: scale(1.05);
            animation: glow 1.5s ease-in-out infinite;
          }
          .modal-cancel-button:hover:not(:disabled) {
            background-color: #B0BEC5;
          }
          .input-field:focus {
            border-color: #6B21A8;
            outline: none;
            box-shadow: 0 0 5px rgba(107, 33, 168, 0.5);
          }
          .upload-container:hover {
            background-color: #E5E7EB;
          }
          .delete-button:hover { color: #FF0000; }
          .checkmark-button:hover { color: #32CD32; }
          .scrollable-content::-webkit-scrollbar {
            width: 8px; // Visible scrollbar for Webkit browsers
            background-color: #f1f1f1;
            border-radius: 4px;
          }
          .scrollable-content::-webkit-scrollbar-thumb {
            background-color: #888;
            border-radius: 4px;
          }
          .scrollable-content::-webkit-scrollbar-thumb:hover {
            background-color: #555;
          }
          .scrollable-content {
            -ms-overflow-style: auto; // Enable scrollbar for IE and Edge
            scrollbar-width: thin; // Enable thin scrollbar for Firefox
          }
          .view-modal::-webkit-scrollbar {
            width: 8px;
          }
          .view-modal::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 4px;
          }
          .view-modal::-webkit-scrollbar-thumb {
            background: #888;
            border-radius: 4px;
          }
          .view-modal::-webkit-scrollbar-thumb:hover {
            background: #555;
          }
          select.input-field {
            background-color: white;
            cursor: pointer;
          }

          select.input-field:disabled {
            background-color: #f3f4f6;
            cursor: not-allowed;
          }
          @keyframes glow {
            0% { box-shadow: 0 0 4px #C5A8FF; }
            50% { box-shadow: 0 0 12px #C5A8FF; }
            100% { box-shadow: 0 0 4px #C5A8FF; }
          }
        `}
      </style>

      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          height: '100%',
          width: sidebarWidth,
          zIndex: 10,
          transition: 'width 0.3s ease',
          ...styles.sidebar,
        }}
      >
        <AdminSidebar />
      </div>

      <div style={{ marginLeft: sidebarWidth, width: `calc(100% - ${sidebarWidth})`, transition: 'margin-left 0.3s ease' }}>
        <AdminNavbar />
        <div style={styles.content}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '0.5rem',
              marginTop: '18px',
              marginBottom: '18px',
              width: 'calc(100% - 30px)',
              marginLeft: '15px',
              marginRight: '15px',
            }}
          >
            <button style={styles.button} onClick={handleOpenModal} className="glow-button">
              Add Student
            </button>
            <button style={styles.button} onClick={handleDownloadTemplate} className="glow-button">
              Download Student CSV
            </button>
          </div>
          <div style={styles.scrollableContent} className="scrollable-content">
            <div style={styles.notificationContainer}>
              {notification.message && (
                <div style={styles.notification}>
                  {notification.message}
                  <FontAwesomeIcon
                    icon={faTimes}
                    style={styles.notificationClose}
                    onClick={handleCloseNotification}
                  />
                </div>
              )}
            </div>
            <div style={styles.cardContainer}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', width: '100%' }}>
                <label style={styles.label}></label>
                <div
                  style={styles.uploadContainer}
                  className="upload-container"
                  onClick={() => document.getElementById('csvFile').click()}
                >
                  <div style={styles.uploadText}>Click here to upload a CSV file</div>
                  <div style={styles.uploadSubText}>Only CSV files allowed</div>
                  <div style={styles.uploadSubText}>All fields are Mandatory</div>
                  <input
                    id="csvFile"
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    disabled={loading}
                    style={{ display: 'none' }}
                  />
                </div>

                {selectedFile && (
                  <div style={styles.filePreview}>
                    <div style={styles.fileInfo}>
                      <div style={styles.fileNameText} title={fileName}>
                        {fileName}
                      </div>
                      <div style={styles.progressBarContainer}>
                        <div style={styles.progressBar}></div>
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#374151', fontFamily: "'Roboto', sans-serif" }}>{fileSize} KB</div>
                    </div>
                    <div style={styles.fileActions}>
                      <button
                        style={{ ...styles.actionButton, color: '#32CD32' }}
                        className="checkmark-button"
                        aria-label="File uploaded"
                      >
                        ✓
                      </button>
                      <button
                        style={{ ...styles.actionButton, color: '#808080' }}
                        className="delete-button"
                        onClick={handleFileClear}
                        aria-label="Delete file"
                      >
                        ✗
                      </button>
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'center', width: '100%', marginTop: '8px' }}>
                  <button
                    type="button"
                    onClick={handleUpload}
                    disabled={loading || !selectedFile}
                    style={{
                      ...styles.button,
                      ...(loading || !selectedFile ? styles.buttonDisabled : {}),
                    }}
                    className="glow-button"
                  >
                    {loading ? 'Uploading...' : 'Upload'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div style={styles.modalNotificationContainer}>
              {modalNotification.message && (
                <div style={styles.notification}>
                  {modalNotification.message}
                  <FontAwesomeIcon
                    icon={faTimes}
                    style={styles.notificationClose}
                    onClick={handleCloseModalNotification}
                  />
                </div>
              )}
            </div>
            <div style={styles.modalHeader}>Add New Student</div>
            <form onSubmit={handleSubmit}>
              <div style={styles.formGrid}>
                <div>
                  <label style={styles.label} htmlFor="fullName">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    style={styles.input}
                    id="fullName"
                    name="fullName"
                    value={studentData.fullName}
                    onChange={handleChange}
                    required
                    className="input-field"
                  />
                </div>
                <div>
                  <label style={styles.label} htmlFor="email">
                    Email *
                  </label>
                  <input
                    type="email"
                    style={styles.input}
                    id="email"
                    name="email"
                    value={studentData.email}
                    onChange={handleChange}
                    required
                    className="input-field"
                  />
                </div>
                <div>
                  <label style={styles.label} htmlFor="admissionNo">
                    Admission No. *
                  </label>
                  <input
                    type="text"
                    style={styles.input}
                    id="admissionNo"
                    name="admissionNo"
                    value={studentData.admissionNo}
                    onChange={handleChange}
                    required
                    className="input-field"
                  />
                </div>
                <div>
                  <label style={styles.label} htmlFor="collegeName">
                    College Name *
                  </label>
                  <select
                    style={styles.input}
                    id="collegeName"
                    name="collegeName"
                    value={studentData.collegeName}
                    onChange={handleChange}
                    required
                    className="input-field"
                    disabled={loadingColleges}
                  >
                    <option value="">
                      {loadingColleges ? 'Loading colleges...' : 'Select a college'}
                    </option>
                    {colleges.map((college, index) => (
                      <option key={index} value={college}>
                        {college}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={styles.label} htmlFor="username">
                    Username *
                  </label>
                  <input
                    type="text"
                    style={styles.input}
                    id="username"
                    name="username"
                    value={studentData.username}
                    onChange={handleChange}
                    required
                    className="input-field"
                  />
                </div>
                <div>
                  <label style={styles.label} htmlFor="password">
                    Password *
                  </label>
                  <input
                    type="password"
                    style={styles.input}
                    id="password"
                    name="password"
                    value={studentData.password}
                    onChange={handleChange}
                    required
                    className="input-field"
                  />
                </div>
              </div>
              <div style={styles.modalButtonContainer}>
                <button
                  type="button"
                  style={styles.modalCancelButton}
                  onClick={handleCloseModal}
                  className="modal-cancel-button"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    ...styles.modalAddButton,
                    ...(loading ? styles.buttonDisabled : {}),
                  }}
                  disabled={loading}
                  className="glow-button"
                >
                  {loading ? (
                    <>
                      <span style={{ display: 'inline-block', marginRight: '10px', fontFamily: 'inherit' }}>↻</span>
                      Adding...
                    </>
                  ) : (
                    'SAVE'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddStudent;